import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Ship, ArrowLeft, MapPin, Loader2, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { renderMarkdownSafe } from "../lib/safeMarkdown";
import { useJsonLd, TAXINEARME_SITE_URL } from "../lib/seo";
import SiteNav from "../components/SiteNav";

const HERO_FALLBACK =
  "https://images.unsplash.com/photo-1516483638261-f4dbaf036963?auto=format&fit=crop&q=80&w=2000";

export default function VeniceHotelDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [hotel, setHotel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/venice-hotels/${slug}`)
      .then((r) => setHotel(r.data))
      .catch((err) => {
        if (err?.response?.status === 404) setNotFound(true);
        else toast.error("Could not load hotel");
      })
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!hotel) return;
    if (hotel.meta_title) document.title = hotel.meta_title;
    return () => { document.title = "TaxiNearMe"; };
  }, [hotel]);

  useJsonLd(
    hotel
      ? [
          {
            id: "tnm-hotel-breadcrumbs",
            data: {
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${TAXINEARME_SITE_URL}/` },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "Venice Hotel Transfers",
                  item: `${TAXINEARME_SITE_URL}/hotels-in-venice`,
                },
                {
                  "@type": "ListItem",
                  position: 3,
                  name: hotel.name,
                  item: `${TAXINEARME_SITE_URL}/hotels-in-venice/${hotel.slug}`,
                },
              ],
            },
          },
          {
            id: "tnm-hotel-service",
            data: {
              "@context": "https://schema.org",
              "@type": "TaxiService",
              name: `Private Airport Transfer to ${hotel.name}`,
              description: `Chauffeured car + water taxi from Venice Marco Polo Airport to ${hotel.name}, with meet & greet included.`,
              url: `${TAXINEARME_SITE_URL}/hotels-in-venice/${hotel.slug}`,
              provider: {
                "@type": "LocalBusiness",
                name: "TaxiNearMe",
                url: `${TAXINEARME_SITE_URL}/`,
              },
              areaServed: [
                { "@type": "AdministrativeArea", name: "Venice" },
                { "@type": "AdministrativeArea", name: "Venice Marco Polo Airport" },
              ],
              offers: {
                "@type": "Offer",
                price: (hotel.airport_price || 0).toFixed(2),
                priceCurrency: "EUR",
                url: `${TAXINEARME_SITE_URL}/hotels-in-venice/${hotel.slug}`,
                availability: "https://schema.org/InStock",
              },
              providerMobility: "Car",
            },
          },
        ]
      : []
  );

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-white/50 gap-2">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading...
      </div>
    );
  }
  if (notFound || !hotel) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#0A0A0A] text-white px-4">
        <h1 className="text-3xl font-serif mb-3">Hotel not found</h1>
        <Link to="/hotels-in-venice" className="text-[#C5A059] hover:underline">← Back to Venice hotels</Link>
      </div>
    );
  }

  const heroImage = hotel.hero_image_url || HERO_FALLBACK;
  const mapEmbed =
    hotel.latitude && hotel.longitude
      ? `https://www.google.com/maps?q=${hotel.latitude},${hotel.longitude}&z=16&output=embed`
      : null;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="venice-hotel-detail-page">
      <SiteNav />
      {/* Hero */}
      <section
        className="relative h-[65vh] flex items-end px-4 sm:px-8 lg:px-16 pt-24 pb-14"
        style={{
          backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.35) 40%, rgba(10,10,10,1) 100%), url(${heroImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="max-w-5xl mx-auto w-full">
          <Link
            to="/hotels-in-venice"
            className="inline-flex items-center gap-1 text-white/70 hover:text-[#C5A059] text-sm mb-4"
            data-testid="hotel-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Venice hotels
          </Link>
          <div className="inline-flex items-center gap-2 text-[#C5A059] text-xs uppercase tracking-widest mb-3">
            <Ship className="w-4 h-4" /> Meet &amp; greet · Water taxi included
          </div>
          <h1 className="text-4xl sm:text-5xl font-serif text-white leading-tight" data-testid="hotel-title">
            {hotel.name}
          </h1>
          {hotel.address && (
            <p className="text-white/60 text-sm mt-3 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#C5A059]" /> {hotel.address}
            </p>
          )}
        </div>
      </section>

      {/* Body: booking CTA + description + map */}
      <section className="max-w-5xl mx-auto px-4 sm:px-8 py-12 grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left column: description */}
        <div className="lg:col-span-2 space-y-6">
          {hotel.short_description && (
            <p className="text-white/75 text-lg leading-relaxed">{hotel.short_description}</p>
          )}
          {hotel.long_description && (
            <div
              className="prose-custom text-white/70 leading-relaxed"
              dangerouslySetInnerHTML={renderMarkdownSafe(hotel.long_description)}
            />
          )}
          {mapEmbed && (
            <div className="border border-white/5 rounded-sm overflow-hidden">
              <iframe
                title={`${hotel.name} map`}
                src={mapEmbed}
                width="100%"
                height="320"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="hotel-map"
              />
            </div>
          )}
        </div>

        {/* Right column: pricing summary + CTA */}
        <aside className="bg-[#111] border border-[#C5A059]/40 rounded-sm p-6 h-fit sticky top-24">
          <div className="text-[10px] uppercase tracking-widest text-[#C5A059] mb-1">Airport transfer</div>
          <div className="text-4xl font-serif text-white mb-2 font-mono">€{(hotel.airport_price || 0).toFixed(0)}</div>
          <p className="text-white/60 text-xs mb-5 leading-relaxed">
            All-inclusive from Venice Marco Polo Airport (VCE). <strong className="text-[#C5A059]">Chauffeur + water taxi + meet &amp; greet included.</strong>
          </p>
          <Button
            className="btn-gold w-full uppercase tracking-widest"
            onClick={() => navigate(`/?hotel=${hotel.slug}`)}
            data-testid="hotel-book-now-btn"
          >
            Book this transfer <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <p className="text-white/40 text-[10px] mt-3 text-center">
            Opens the booking form with Airport → {hotel.name} preselected.
          </p>
        </aside>
      </section>

      {/* Bottom CTA — see all routes */}
      <section className="max-w-5xl mx-auto px-4 sm:px-8 pb-16">
        <Link
          to="/hotels-in-venice"
          className="inline-flex items-center gap-2 text-[#C5A059] hover:underline"
        >
          <ArrowLeft className="w-4 h-4" /> More Venice hotels
        </Link>
      </section>
    </div>
  );
}
